import { useCallback, useState } from 'react';
import { apiClient } from '@/api/client';

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, cb: (resp: unknown) => void) => void;
    };
  }
}

let scriptPromise: Promise<boolean> | null = null;

/** Load Razorpay checkout.js once, on demand (not in the main bundle). */
function loadCheckoutScript(): Promise<boolean> {
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptPromise = null; // allow retry
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

export type CheckoutResult =
  | { ok: true; planCode: string }
  | { ok: false; reason: 'cancelled' | 'failed' | 'error'; message: string };

/**
 * Razorpay Standard Checkout flow:
 * create order (server-side amount) → open modal → verify signature server-side.
 * The KEY_SECRET never reaches this code; key_id arrives from the backend.
 */
export function useRazorpayCheckout() {
  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  // couponCode is passed straight through to the server, which re-validates it
  // and derives the amount itself — the client never sends a price.
  const checkout = useCallback(async (planCode: string, couponCode?: string): Promise<CheckoutResult> => {
    setBusyPlan(planCode);
    try {
      const loaded = await loadCheckoutScript();
      if (!loaded || !window.Razorpay) {
        return { ok: false, reason: 'error', message: 'Could not load the payment window. Check your connection and try again.' };
      }

      // 1. Create the order (amount comes from the plan on the server).
      const orderRes = await apiClient.post('/billing/orders', {
        plan_code: planCode,
        ...(couponCode ? { coupon_code: couponCode } : {}),
      });
      const order = (orderRes as any).data?.data ?? (orderRes as any).data;
      if (!order?.order_id) throw new Error('Order creation failed');

      // 2. Open the modal and wait for the user to finish/cancel/fail.
      return await new Promise<CheckoutResult>((resolve) => {
        const rzp = new window.Razorpay!({
          key: order.key_id,
          order_id: order.order_id,
          amount: order.amount,
          currency: order.currency,
          name: 'Defellix',
          description: order.plan_name,
          prefill: { email: order.email || undefined },
          theme: { color: '#3cb44f' },
          modal: {
            ondismiss: () => resolve({ ok: false, reason: 'cancelled', message: 'Payment cancelled.' }),
          },
          handler: async (resp: {
            razorpay_payment_id: string;
            razorpay_order_id: string;
            razorpay_signature: string;
          }) => {
            // 3. Verify the signature server-side — only then is it a sale.
            try {
              await apiClient.post('/billing/verify', {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              resolve({ ok: true, planCode });
            } catch (err: any) {
              resolve({
                ok: false,
                reason: 'error',
                message: err?.response?.data?.message || 'Payment received but verification failed — contact support@defellix.com with your payment reference.',
              });
            }
          },
        });
        rzp.on('payment.failed', (resp: any) => {
          resolve({
            ok: false,
            reason: 'failed',
            message: resp?.error?.description || 'Payment failed. No money was captured — please try again.',
          });
        });
        rzp.open();
      });
    } catch (err: any) {
      if (err?.response?.status === 401) {
        return { ok: false, reason: 'error', message: 'Please log in to purchase a plan.' };
      }
      return { ok: false, reason: 'error', message: err?.response?.data?.message || 'Could not start the payment. Please try again.' };
    } finally {
      setBusyPlan(null);
    }
  }, []);

  return { checkout, busyPlan };
}
